// ════════════════════════════════════════════════════════════════════════
//  Kindblick – ein Kind, alle Lehrgänge
//
//  Die Ansicht fürs Elterngespräch. Und der erste Ort, an dem die Lektionen
//  ihre TITEL zeigen: in der Übersicht ist je Lektion ein acht Pixel breites
//  Feld, hier stehen sie untereinander und haben Platz.
//
//  Es steht kein Vergleich mit anderen Kindern darauf. Was hier fehlt, fehlt
//  absichtlich: Rang, Klassenmittel, Bewertung.
// ════════════════════════════════════════════════════════════════════════

import { Link, useParams } from "react-router-dom";
import type { Bundle } from "@otto/lehrgang";
import { useSession } from "@otto/plattform";
import { useClass } from "../../classes/lib/data.js";
import { useClassCourses, type ClassCourse } from "../../courses/lib/data.js";
import { useSchool } from "../../school/lib/data.js";
import { useCertificateDownload } from "../../certificates/hooks/useCertificateDownload.js";
import { summarize } from "../../certificates/lib/summary.js";
import { useResults } from "../lib/data.js";
import { courseProgress, type BandCell } from "../lib/overview.js";
import { exactDate, lastPractised } from "../lib/lastPractised.js";
import { showGain, showWpm } from "../lib/speed.js";
import own from "./StudentDetail.module.css";
import styles from "../../../shared/components/shell.module.css";

/** Drei Plätze, immer. Eine Zeichenkette aus einem bis drei Sternen ließe sich
 *  untereinander nicht vergleichen – das Auge zählt sonst jede Zeile neu. */
function Stars({ stars }: { stars: 1 | 2 | 3 | null }) {
  const count = stars ?? 0;
  return (
    <span
      className={own.stars}
      aria-label={count === 1 ? "ein Stern" : `${count} Sterne`}
    >
      <span className={own.starOn}>{"★".repeat(count)}</span>
      <span className={own.starOff}>{"★".repeat(3 - count)}</span>
    </span>
  );
}

function LessonRow({ cell }: { cell: BandCell }) {
  return (
    <li className={own.lesson}>
      <span className={own.number}>{cell.number}</span>
      <span className={own.name}>
        {cell.title} <span className={own.subtitle}>· {cell.subtitle}</span>
      </span>
      {cell.mark === null ? (
        <span className={own.quiet}>noch nicht begonnen</span>
      ) : (
        <>
          <Stars stars={cell.mark.stars} />
          <span className={own.stages}>
            {cell.stagesDone} von {cell.stagesTotal} Stufen
          </span>
          {!cell.mark.complete && (
            <span className={own.open}>noch nicht alle Stufen</span>
          )}
        </>
      )}
    </li>
  );
}

export function StudentDetail() {
  const { classId = "", code = "" } = useParams();
  const { client, state } = useSession();
  const schoolId =
    state.kind === "signedIn" ? (state.identity.profile?.schoolId ?? "") : "";
  const schoolClass = useClass(client, classId);
  const school = useSchool(client, schoolId);
  const courses = useClassCourses(client, classId);
  const results = useResults(client, classId);
  const certificate = useCertificateDownload();

  const row = (results.data ?? []).find((entry) => entry.code === code) ?? null;
  const readable = (courses.data ?? []).filter(
    (course): course is ClassCourse & { bundle: Bundle } => course.bundle !== null,
  );

  const back = (
    <p className={styles.subtitle} style={{ marginBottom: 10 }}>
      <Link to={`/classes/${classId}/results`}>← Ergebnisse</Link>
    </p>
  );

  if (results.isPending || courses.isPending) {
    return (
      <div className={styles.card}>
        {back}
        <p className={styles.empty}>Wird geladen …</p>
      </div>
    );
  }

  if (row === null) {
    return (
      <div className={styles.card}>
        {back}
        <h1 className={styles.title}>{code}</h1>
        <p className={styles.empty}>
          Diesen Übungscode gibt es in dieser Klasse nicht (mehr).
        </p>
      </div>
    );
  }

  return (
    <div className={styles.card}>
      {back}
      <div className={own.head}>
        <h1 className={`${styles.title} ${own.code}`} style={{ margin: 0 }}>
          {code}
        </h1>
        <span className={own.quiet} title={exactDate(row.lastAt) ?? undefined}>
          zuletzt geübt: {lastPractised(row.lastAt, new Date())}
        </span>
      </div>
      <p className={styles.subtitle}>
        {schoolClass.data ? `${schoolClass.data.name} · ` : ""}
        Auf der Plattform steht kein Name – wer sich hinter diesem Code verbirgt, wissen
        nur Sie.
      </p>

      {readable.length === 0 && (
        <p className={styles.empty}>
          Dieser Klasse ist noch kein lesbarer Lehrgang zugeordnet.
        </p>
      )}

      {readable.map((course) => {
        const bundle = course.bundle;
        const progress = courseProgress(row, bundle);
        const summary = summarize(row, bundle);
        return (
          <section key={course.curriculumId} className={own.course}>
            <div className={own.courseHead}>
              <h2 className={own.courseTitle}>
                {course.title}{" "}
                <span className={own.quiet}>· Fassung {course.version}</span>
              </h2>
              <span className={own.quiet}>
                {progress.stagesDone} von {progress.stagesTotal} Stufen · {progress.stars}{" "}
                Sterne
              </span>
            </div>

            <ul className={own.lessons}>
              {progress.lessons.map((cell) => (
                <LessonRow key={cell.key} cell={cell} />
              ))}
            </ul>

            {bundle.assessment && (
              <p className={own.tempo}>
                {progress.speed === null ? (
                  <span className={own.quiet}>
                    Tempo: noch nicht gemessen. Der Test wird vor der ersten Lektion
                    getippt.
                  </span>
                ) : (
                  <>
                    Tempo: am Anfang {showWpm(progress.speed.before)} · am Ende{" "}
                    {showWpm(progress.speed.after)} Wörter pro Minute{" "}
                    <strong>({showGain(progress.speed.gain)})</strong>
                  </>
                )}
              </p>
            )}

            {summary !== null && (
              <p className={styles.row} style={{ marginTop: 12 }}>
                <button
                  type="button"
                  className={styles.buttonQuiet}
                  disabled={certificate.printing}
                  onClick={() => {
                    void certificate.download({
                      schoolClass: schoolClass.data?.name ?? "",
                      schoolYear: schoolClass.data?.school_year ?? "",
                      schoolName: school.data?.name ?? "",
                      courseTitle: course.title,
                      courseSubtitle: course.subtitle,
                      students: [{ code, summary }],
                    });
                  }}
                >
                  {certificate.printing ? "Wird erzeugt …" : "Urkunde für dieses Kind"}
                </button>
              </p>
            )}
          </section>
        );
      })}
    </div>
  );
}
